/**
 * Reference-counted body scroll lock shared by every modal surface
 * (reminder popup, global promo popup, checkout sheet/drawer).
 *
 * Why ref-counted: each modal used to snapshot + restore body styles
 * independently. When two overlapped (popup fires while checkout is open,
 * or vice versa), whichever closed LAST restored the other's
 * `position: fixed; top: -N px` snapshot — permanently freezing scroll and
 * resetting scrollY to 0 (transparent navbar, stuck page). With one counter,
 * the styles are saved exactly once by the first lock and restored exactly
 * once by the last unlock, in any open/close order.
 *
 * `position: fixed` (not just overflow: hidden) because iOS Safari ignores
 * body overflow for touch scrolling.
 */
import { useEffect } from "react";
import { getLenis } from "@/lib/smooth-scroll";

let lockCount = 0;
let savedScrollY = 0;
let savedStyles: {
  overflow: string;
  position: string;
  top: string;
  left: string;
  right: string;
  width: string;
} | null = null;

export function lockBodyScroll(): void {
  if (typeof document === "undefined") return;
  lockCount++;
  if (lockCount > 1) return; // already locked by another modal
  // 2026-09-09: Lenis kept running while <body> was `position: fixed`. A fixed
  // body collapses documentElement.scrollHeight to the viewport, so Lenis's
  // (250ms-debounced) ResizeObserver clamped its scroll limit to 0 and every
  // target after that was clamped with it. Stop it for the duration of the lock.
  getLenis()?.stop();
  const b = document.body.style;
  savedScrollY = window.scrollY;
  savedStyles = {
    overflow: b.overflow,
    position: b.position,
    top: b.top,
    left: b.left,
    right: b.right,
    width: b.width,
  };
  b.overflow = "hidden";
  b.position = "fixed";
  b.top = `-${savedScrollY}px`;
  b.left = "0";
  b.right = "0";
  b.width = "100%";
}

export function unlockBodyScroll(): void {
  if (typeof document === "undefined") return;
  if (lockCount === 0) return; // defensive: unlock without lock
  lockCount--;
  if (lockCount > 0) return; // another modal still open
  const b = document.body.style;
  if (savedStyles) {
    b.overflow = savedStyles.overflow;
    b.position = savedStyles.position;
    b.top = savedStyles.top;
    b.left = savedStyles.left;
    b.right = savedStyles.right;
    b.width = savedStyles.width;
    savedStyles = null;
  }
  window.scrollTo(0, savedScrollY);
  // 2026-09-09: the native scrollTo above restores the page, but Lenis's own
  // model was still at limit 0 until its 250ms debounce caught up — so a wheel
  // notch within that window animated the page to the top. resize() recomputes
  // the limit now and the immediate scrollTo re-seats Lenis at the real position.
  const l = getLenis();
  if (l) {
    l.start();
    l.resize();
    l.scrollTo(savedScrollY, { immediate: true, force: true });
  }
}

/**
 * The page's real scroll position, even while a modal holds the lock.
 *
 * The lock pins <body> with `position: fixed; top: -Ypx`, which makes
 * `window.scrollY` read 0 and fires a scroll event. Anything that styles by
 * scroll depth (the navbar's background) then thinks the page jumped to the
 * top and goes transparent behind the popup (Esther, 2026-10-08). Read this
 * instead of `window.scrollY`.
 */
export function effectiveScrollY(): number {
  if (typeof window === "undefined") return 0;
  return lockCount > 0 ? savedScrollY : window.scrollY;
}

/** Effect wrapper: lock while `active`, release on close/unmount. */
export function useBodyScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    lockBodyScroll();
    return () => unlockBodyScroll();
  }, [active]);
}
